import { DialogWithForm } from '@strata/react-dialog';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import dayjs from 'dayjs';
import { Formik, type FormikProps } from 'formik';
import * as Yup from 'yup';

import { GeneralCommunicationForm } from 'components/intakeForm/GeneralCommunicationForm';
import { PrescriberCommunicationForm } from 'components/intakeForm/PrescriberCommunicationForm';
import { SubmissionAttemptForm } from 'components/intakeForm/SubmissionAttemptForm';
import { ActionPlan } from 'services/constants';
import { type IntakeCase, usePostLogPriorAuthActivityMutation } from 'store/api/intakeCaseApi';

interface LogPriorAuthCommunicationDialogProps {
    isOpen: boolean;
    onClose: () => void;
    intakeCase: Pick<
        IntakeCase,
        | 'intakeCaseId'
        | 'outreaches'
        | 'quantity'
        | 'daysSupply'
        | 'strength'
        | 'bin'
        | 'pcn'
        | 'rxGroup'
    >;
    isValidIntakeCase: boolean;
}

const PriorAuthActivityType = {
    PRESCRIBER: 'Action plan communication',
    SUBMISSION_ATTEMPT: 'Submission attempt',
    GENERAL_COMMUNICATION: 'General Communication',
} as const;

const ValidationSchema = Yup.object({
    priorAuthActivityType: Yup.string()
        .oneOf(Object.values(PriorAuthActivityType), 'Please select a valid activity type')
        .required('Prior Auth Activity is required'),

    priorAuthActivityDate: Yup.string()
        .required('Date is required')
        .test('valid-date', 'Please enter a valid date', (value) => {
            if (!value) return false;
            return dayjs(value).isValid();
        }),

    communicationDirection: Yup.string().when(['priorAuthActivityType', 'isPriorAuthSubmitted'], {
        is: (activityType: string, isSubmitted: boolean) =>
            activityType === PriorAuthActivityType.PRESCRIBER ||
            activityType === PriorAuthActivityType.GENERAL_COMMUNICATION ||
            (activityType === PriorAuthActivityType.SUBMISSION_ATTEMPT && isSubmitted !== true),
        then: (schema) =>
            schema
                .min(1, 'Communication Direction is required')
                .required('Communication Direction is required'),
        otherwise: (schema) => schema.nullable(),
    }),

    isPrescriberOutreachComplete: Yup.boolean().when('priorAuthActivityType', {
        is: PriorAuthActivityType.PRESCRIBER,
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema.nullable(),
    }),

    communicationType: Yup.string().when(['priorAuthActivityType', 'isPriorAuthSubmitted'], {
        is: (activityType: string, isSubmitted: boolean) =>
            activityType === PriorAuthActivityType.PRESCRIBER ||
            (activityType === PriorAuthActivityType.SUBMISSION_ATTEMPT && isSubmitted !== true),
        then: (schema) =>
            schema
                .min(1, 'Communication Type is required')
                .required('Communication Type is required'),
        otherwise: (schema) => schema.nullable(),
    }),

    actionPlan: Yup.string().when(['priorAuthActivityType', 'isPrescriberOutreachComplete'], {
        is: (activityType: string, isComplete: boolean) =>
            activityType === PriorAuthActivityType.PRESCRIBER && isComplete === true,
        then: (schema) =>
            schema
                .oneOf(Object.values(ActionPlan), 'Please select a valid action plan')
                .required('Action Plan is required when completing prescriber outreach'),
        otherwise: (schema) => schema.nullable(),
    }),

    isPriorAuthSubmitted: Yup.boolean().when('priorAuthActivityType', {
        is: PriorAuthActivityType.SUBMISSION_ATTEMPT,
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema.nullable(),
    }),

    note: Yup.string().max(1000, 'Note cannot exceed 1000 characters').nullable(),

    outreachOrganization: Yup.string().when(['priorAuthActivityType', 'isPriorAuthSubmitted'], {
        is: (activityType: string, isSubmitted: boolean) =>
            (activityType !== PriorAuthActivityType.PRESCRIBER &&
                activityType !== PriorAuthActivityType.SUBMISSION_ATTEMPT) ||
            (activityType === PriorAuthActivityType.SUBMISSION_ATTEMPT && isSubmitted !== true),
        then: (schema) =>
            schema
                .min(1, 'Outreach Organization is required')
                .required('Outreach Organization is required'),
        otherwise: (schema) => schema.nullable(),
    }),

    submitId: Yup.string().when('isPriorAuthSubmitted', {
        is: true,
        then: (schema) => schema.required('Submit ID is required when prior auth is submitted'),
        otherwise: (schema) => schema.nullable(),
    }),

    submitMethod: Yup.string().when('isPriorAuthSubmitted', {
        is: true,
        then: (schema) => schema.required('Submit Method is required when prior auth is submitted'),
        otherwise: (schema) => schema.nullable(),
    }),
});

export type PriorAuthFormValues = Yup.InferType<typeof ValidationSchema>;

export const LogPriorAuthCommunicationDialog = ({
    isOpen,
    onClose,
    intakeCase,
    isValidIntakeCase,
}: LogPriorAuthCommunicationDialogProps) => {
    const now = dayjs();
    const lastOutreach = intakeCase.outreaches?.[intakeCase.outreaches.length - 1];
    const isPrescriberOutreachComplete = !!lastOutreach?.isComplete;
    const actionPlan = lastOutreach?.actionPlan;
    const [postLogPriorAuthCommunication] = usePostLogPriorAuthActivityMutation();

    const requiredEpaFields = [
        {
            label: 'Quantity',
            isValid: (intakeCase.quantity ?? 0) > 0,
        },
        {
            label: 'Days Supply',
            isValid: (intakeCase.daysSupply ?? 0) > 0,
        },
        {
            label: 'Strength',
            isValid: (intakeCase.strength?.trim() ?? '') !== '',
        },
        { label: 'BIN', isValid: (intakeCase.bin?.trim() ?? '') !== '' },
        { label: 'PCN', isValid: (intakeCase.pcn?.trim() ?? '') !== '' },
        {
            label: 'Rx Group',
            isValid: (intakeCase.rxGroup?.trim() ?? '') !== '',
        },
    ];

    const missingEpaFields = requiredEpaFields
        .filter((field) => !field.isValid)
        .map((field) => field.label);

    const isValidForEPaSubmission = missingEpaFields.length === 0;

    return (
        <Formik<PriorAuthFormValues>
            initialValues={
                {
                    priorAuthActivityDate: now.toISOString(),
                    isPrescriberOutreachComplete,
                    actionPlan,
                } as Yup.InferType<typeof ValidationSchema>
            }
            validationSchema={ValidationSchema}
            enableReinitialize={false}
            onSubmit={async (values, { resetForm, setFieldError }) => {
                const isOutreachComplete =
                    isPrescriberOutreachComplete || values.isPrescriberOutreachComplete;
                const effectiveActionPlan = isOutreachComplete
                    ? (values.actionPlan ?? actionPlan)
                    : values.actionPlan;
                if (values.priorAuthActivityType === PriorAuthActivityType.PRESCRIBER) {
                    if (isOutreachComplete && !effectiveActionPlan) {
                        setFieldError(
                            'actionPlan',
                            'Action Plan is required when completing prescriber outreach',
                        );
                        return;
                    }
                    if (
                        effectiveActionPlan === ActionPlan.FMH_TO_SUBMIT &&
                        !isValidForEPaSubmission
                    ) {
                        setFieldError(
                            'quantity',
                            'At least quantity is required in the intake form to log an action plan of FMH to submit',
                        );
                        return;
                    }
                }
                await postLogPriorAuthCommunication({
                    intakeCaseId: intakeCase.intakeCaseId,
                    priorAuthActivityType: values.priorAuthActivityType,
                    priorAuthActivityDate: values.priorAuthActivityDate,
                    communicationDirection: values.communicationDirection ?? '',
                    isPrescriberOutreachComplete: isOutreachComplete,
                    communicationType: values.communicationType,
                    actionPlan: effectiveActionPlan,
                    note: values.note ?? '',
                    isPriorAuthSubmitted: values.isPriorAuthSubmitted,
                    outreachOrganization: values.outreachOrganization,
                    submitId: values.submitId,
                    submitMethod: values.submitMethod,
                }).unwrap();
                SuccessToast({ message: 'Communication Logged Successfully.' });
                resetForm();
                onClose();
            }}
        >
            {(formik: FormikProps<PriorAuthFormValues>) => (
                <DialogWithForm
                    isOpen={isOpen}
                    dialogTitle="Log Prior Auth Activity"
                    primaryButtonLabel="Log Prior Auth Activity"
                    secondaryButtonLabel="Cancel"
                    isPrimaryButtonDisabled={
                        !isValidIntakeCase ||
                        !formik.isValid ||
                        !formik.dirty ||
                        !(
                            isValidForEPaSubmission ||
                            formik.values.actionPlan !== ActionPlan.FMH_TO_SUBMIT ||
                            formik.values.priorAuthActivityType !== PriorAuthActivityType.PRESCRIBER
                        )
                    }
                    onSecondaryButtonClick={(e) => {
                        onClose();
                        formik.handleReset(e);
                    }}
                    dialogContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="priorAuthActivityType"
                                label="Prior Auth Activity"
                                placeholderText="Select"
                                required
                                values={Object.values(PriorAuthActivityType)}
                                onChange={(newValue) => {
                                    void (async () => {
                                        const isPrescriber =
                                            newValue === PriorAuthActivityType.PRESCRIBER;
                                        await Promise.all([
                                            formik.setFieldValue('priorAuthActivityType', newValue),
                                            formik.setFieldValue(
                                                'communicationDirection',
                                                undefined,
                                            ),
                                            formik.setFieldValue('communicationType', undefined),
                                            formik.setFieldValue('outreachOrganization', undefined),
                                            formik.setFieldValue(
                                                'isPrescriberOutreachComplete',
                                                isPrescriber
                                                    ? !!isPrescriberOutreachComplete
                                                    : undefined,
                                            ),
                                            formik.setFieldValue(
                                                'actionPlan',
                                                isPrescriber && isPrescriberOutreachComplete
                                                    ? actionPlan
                                                    : undefined,
                                            ),
                                            formik.setFieldValue('isPriorAuthSubmitted', undefined),
                                            formik.setFieldValue('submitMethod', undefined),
                                            formik.setFieldValue('submitId', undefined),
                                        ]);
                                    })();
                                }}
                                disableAutoPopulate
                            />
                            {formik.values.priorAuthActivityType ===
                                PriorAuthActivityType.PRESCRIBER && (
                                <PrescriberCommunicationForm
                                    formik={formik}
                                    isPrescriberOutreachComplete={isPrescriberOutreachComplete}
                                    missingEpaFields={missingEpaFields}
                                />
                            )}

                            {formik.values.priorAuthActivityType ===
                                PriorAuthActivityType.SUBMISSION_ATTEMPT && (
                                <SubmissionAttemptForm formik={formik} />
                            )}

                            {formik.values.priorAuthActivityType ===
                                PriorAuthActivityType.GENERAL_COMMUNICATION && (
                                <GeneralCommunicationForm />
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};
